import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import type { Signature, SignatureForm, PaginationParams, PaginationResponse } from '@/types'
import apiClient from '@/lib/api/client'
import { getMySignatures as sdkGetSignatures, getMySignatureById as sdkGetSignatureById, createMySignature as sdkCreateSignature, updateMySignature as sdkUpdateSignature, deleteMySignature as sdkDeleteSignature } from '@/lib/api/generated/sdk.gen'

// 获取签名列表
export const useSignatures = (params: PaginationParams & { 
  search?: string
  status?: string
  type?: string
}) => {
  return useQuery({
    queryKey: ['signatures', params],
    queryFn: async (): Promise<PaginationResponse<Signature>> => {
      const res: any = await sdkGetSignatures({ client: apiClient, responseStyle: 'fields', throwOnError: false, query: params as any })
      const payload: any = (res as any)?.data ?? res
      
      // 转换后端数据结构为前端期望的格式
      if (payload && payload.records) {
        // 转换字段名映射
        const convertedRecords = payload.records.map((record: any) => ({
          id: record.id.toString(), // 确保ID是字符串类型
          name: record.signatureName, // signatureName -> name
          content: record.fullSignatureFormat || record.signatureCode, // 优先使用fullSignatureFormat，兼容旧数据
          fullSignatureFormat: record.fullSignatureFormat, // 保留原始字段
          type: record.signatureType, // signatureType -> type
          status: record.status,
          usageType: record.usageType,
          certificate: record.certificateUrl,
          authorizationUrl: record.authorizationUrl,
          applicantName: record.applicantName,
          applicantPhone: record.applicantPhone,
          applicantIdCard: record.applicantIdCard,
          applyReason: record.applyReason,
          reviewComment: record.reviewComment,
          usageCount: record.usageCount,
          lastUsedAt: record.lastUsedAt,
          createdAt: record.createdAt,
          updatedAt: record.updatedAt,
          // 运营商报备相关字段
          operatorReportStatus: record.operatorReportStatus,
          operatorReportTime: record.operatorReportTime,
          operatorReportComment: record.operatorReportComment,
          operatorReportId: record.operatorReportId,
          operatorReportRequiredDocs: record.operatorReportRequiredDocs,
          // 运营商报备详情（需要从API获取）
          operatorReports: record.operatorReports || [],
          // 更丰富的申请资料字段
          trademarkCertificateUrl: record.trademarkCertificateUrl,
          trademarkQueryScreenshotUrl: record.trademarkQueryScreenshotUrl,
          appStoreInfo: record.appStoreInfo,
          icpFilingScreenshotUrl: record.icpFilingScreenshotUrl,
          softwareCopyrightUrl: record.softwareCopyrightUrl,
          domainCertificateUrl: record.domainCertificateUrl,
          organizationCodeUrl: record.organizationCodeUrl
        }))
        
        return {
          content: convertedRecords,
          totalElements: payload.total,
          totalPages: payload.pages,
          size: payload.size,
          number: payload.current,
          first: payload.current === 1,
          last: payload.current === payload.pages
        } as PaginationResponse<Signature>
      }
      
      return payload as PaginationResponse<Signature>
    },
  })
}

// 获取签名详情
export const useSignature = (id: string) => {
  return useQuery({
    queryKey: ['signature', id],
    queryFn: async (): Promise<Signature> => {
      const res: any = await sdkGetSignatureById({ client: apiClient, responseStyle: 'fields', throwOnError: false, path: { id } as any })
      return ((res as any)?.data ?? res) as Signature
    },
    enabled: !!id,
  })
}

// 创建签名
export const useCreateSignature = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (data: SignatureForm): Promise<Signature> => {
      // 转换前端数据格式为后端期望的格式
      const requestData = {
        signatureName: data.name,
        signatureType: data.type,
        usageType: data.usageType,
        applicantName: data.applicantName,
        applicantPhone: data.applicantPhone,
        applicantIdCard: data.applicantIdCard,
        applyReason: data.applyReason,
        certificateUrl: null, // 暂时不处理文件上传
        authorizationUrl: null, // 暂时不处理文件上传
      }
      
      const res: any = await sdkCreateSignature({ client: apiClient, responseStyle: 'fields', throwOnError: false, body: requestData as any })
      return ((res as any)?.data ?? res) as Signature
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['signatures'] })
    },
  })
}

// 更新签名
export const useUpdateSignature = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: Partial<SignatureForm> }): Promise<Signature> => {
      const res: any = await sdkUpdateSignature({ client: apiClient, responseStyle: 'fields', throwOnError: false, path: { id } as any, body: data as any })
      return ((res as any)?.data ?? res) as Signature
    },
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: ['signatures'] })
      queryClient.invalidateQueries({ queryKey: ['signature', id] })
    },
  })
}

// 删除签名
export const useDeleteSignature = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (id: string): Promise<void> => {
      await sdkDeleteSignature({ client: apiClient, responseStyle: 'fields', throwOnError: false, path: { id } as any })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['signatures'] })
    },
  })
}